import React, {
	createContext,
	useContext,
	useState,
	useCallback,
	ReactNode
} from "react";
import { Snackbar, Alert, AlertColor, SnackbarOrigin } from "@mui/material";
import { useTranslation } from "react-i18next";

export interface SnackbarMessage {
	id: string;
	message: string;
	severity?: AlertColor;
	duration?: number;
	action?: ReactNode;
	customSx?: object;
}

export interface SnackbarContextValue {
	showSnackbar: (message: string | Omit<SnackbarMessage, "id">) => string;
	showSuccess: (message: string) => string;
	showError: (message: string) => string;
	showWarning: (message: string, duration: number) => string;
	showInfo: (message: string) => string;
	hideSnackbar: (id: string) => void;
	hideAllSnackbars: () => void;
}

const SnackbarContext = createContext<SnackbarContextValue | undefined>(undefined);

export interface SnackbarProviderProps {
	children: ReactNode;
	maxSnackbars?: number;
	anchorOrigin?: SnackbarOrigin;
	defaultDuration?: number;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({
	children,
	maxSnackbars = 3,
	anchorOrigin = { vertical: "bottom", horizontal: "left" },
	defaultDuration = 6000
}) => {
	const [snackbars, setSnackbars] = useState<SnackbarMessage[]>([]);

	const generateId = useCallback(() => {
		return `snackbar-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
	}, []);

	const { t } = useTranslation();

	const showSnackbar = useCallback(
		(messageOrConfig: string | Omit<SnackbarMessage, "id">) => {
			const id = generateId();

			const newSnackbar: SnackbarMessage =
				typeof messageOrConfig === "string"
					? {
							id,
							message: messageOrConfig,
							severity: "info",
							duration: defaultDuration
					  }
					: {
							id,
							severity: "info",
							duration: defaultDuration,
							...messageOrConfig
					  };

			setSnackbars(prev => {
				const updated = [...prev, newSnackbar];
				// Keep only the most recent snackbars
				return updated.slice(-maxSnackbars);
			});

			return id;
		},
		[defaultDuration, maxSnackbars, generateId]
	);

	const showSuccess = useCallback(
		(message: string) => {
			return showSnackbar({ message: t(message), severity: "success" });
		},
		[showSnackbar, t]
	);

	const showError = useCallback(
		(message: string) => {
			return showSnackbar({ message: t(message), severity: "error" });
		},
		[showSnackbar, t]
	);

	const showWarning = useCallback(
		(message: string, duration: number = 10000) => {
			return showSnackbar({ message: t(message), severity: "warning", duration });
		},
		[showSnackbar, t]
	);

	const showInfo = useCallback(
		(message: string) => {
			return showSnackbar({ message: t(message), severity: "info" });
		},
		[showSnackbar, t]
	);

	const hideSnackbar = useCallback((id: string) => {
		setSnackbars(prev => prev.filter(snackbar => snackbar.id !== id));
	}, []);

	const hideAllSnackbars = useCallback(() => {
		setSnackbars([]);
	}, []);

	const handleClose = useCallback(
		(id: string) => (_event?: React.SyntheticEvent | Event, reason?: string) => {
			if (reason === "clickaway") {
				return;
			}
			hideSnackbar(id);
		},
		[hideSnackbar]
	);

	const contextValue: SnackbarContextValue = {
		showSnackbar,
		showSuccess,
		showError,
		showWarning,
		showInfo,
		hideSnackbar,
		hideAllSnackbars
	};

	return (
		<SnackbarContext.Provider value={contextValue}>
			{children}
			{snackbars.map((snackbar, index) => (
				<Snackbar
					key={snackbar.id}
					open={true}
					autoHideDuration={snackbar.duration}
					onClose={handleClose(snackbar.id)}
					onClick={handleClose(snackbar.id)}
					anchorOrigin={{
						...anchorOrigin,
						// Stack multiple snackbars vertically
						vertical: anchorOrigin.vertical === "bottom" ? "bottom" : "top"
					}}
					sx={{
						// Position multiple snackbars with proper spacing
						...(snackbars.length > 1 && {
							position: "relative",
							[anchorOrigin.vertical === "bottom" ? "bottom" : "top"]: `${index * 70}px`
						}),
						// Global styling for better text wrapping and width
						maxWidth: "800px",
						width: "auto",
						// Apply custom styling if provided
						...snackbar.customSx
					}}
					action={snackbar.action}
				>
					<Alert
						onClose={handleClose(snackbar.id)}
						onClick={handleClose(snackbar.id)}
						severity={snackbar.severity}
						variant="filled"
						sx={{
							width: "100%",
							whiteSpace: "pre-wrap",
							wordBreak: "break-word"
						}}
					>
						{snackbar.message}
					</Alert>
				</Snackbar>
			))}
		</SnackbarContext.Provider>
	);
};

export const useSnackbarContext = (): SnackbarContextValue => {
	const context = useContext(SnackbarContext);

	if (context === undefined) {
		throw new Error("useSnackbarContext must be used within a SnackbarProvider");
	}

	return context;
};
